/* eslint-disable complexity */
// import classNames from 'classnames';
import lodash from 'lodash';
import {observable, action, computed, runInAction} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import {Alert} from 'reactstrap';

import OrderDetails from '../onboarding/components/orderDetails/OrderDetails';
import ChargebeeCheckout from '../../common/chargebeeCheckout/ChargebeeCheckout';
import Link from '../../common/link/Link';
import PricingTabs from '../../common/pricingTabs/PricingTabs';
import MediaStickPricingTabs from '../../common/pricingTabs/MediaStickPricingTabs';
import OnboardUserSigns from '../onboarding/components/onboardUserSigns/OnboardUserSigns';
import inject from '../../hoc/injectHoc';
import {dashboardRoute} from '../../routePaths';
import {STATE_PRE, STATE_PENDING} from '../../../constants/asyncConstants';
import LoadingIcon from '../../common/loadingIcon/LoadingIcon';
import {ALA_CARTE_CREDITS, MEDIA_STICK_CREDITS} from '../../../constants/pricingConstants';

import './userPaymentPage.scss';
import UpdatePlanAlert from './components/updatePlanAlert/UpdatePlanAlert';
import CannotUpdatePlanAlert from './components/cannotUpdatePlanAlert/CannotUpdatePlanAlert';
import {
  UPDATE_PLAN_REASON_TRIAL,
  UPDATE_PLAN_REASON_COUPON,
  UPDATE_PLAN_REASON_SUBSCRIPTION_DELETED,
  PAYMENT_PLAN_MONTHLY,
  PAYMENT_PLAN_LIFETIME
} from '../../../constants/subscriptionPlanConstants';
import FlowStepper from '../../common/flowStepper/FlowStepper';
import {parseSearchParams} from '../../../utils/routeHelper';

const PLANS_SECTION_KEY = 1;
const ADDONS_SECTION_KEY = 2;
const CHECKOUT_SECTION_KEY = 3;

const USER_PAYMENT_FLOW_SECTIONS = [{
  key: PLANS_SECTION_KEY,
  label: 'Plans'
}, {
  key: ADDONS_SECTION_KEY,
  label: 'Add-ons'
}, {
  key: CHECKOUT_SECTION_KEY,
  label: 'Checkout'
}];

/**
 * The UserPaymentPage component.
 */
export class UserPaymentPage extends React.Component {
  /**
   * Current section displayed in the onboarding process
   * Should only use the constants 1 or 2
   *
   * @type {string}
   */
  @observable currentSection = 1;

  /**
   * The currently selected plan.
   * This will be selected in the SubscriptionPlanTable component.
   *
   * @type {object}
   */
  @observable currentPlan = null;

  /**
   * The starting plan id.
   * This will clear when the plan is selected.
   *
   * @type {?number}
   */
  @observable startingPlanId = null;

  /**
   * The starting credit data.
   * This will clear when the plan or credits are selected.
   *
   * @type {?number}
   */
  @observable startingAlaCarteCredit = null;

  /**
   * The a la carte credits the user wants to purchase
   *
   * @type {?{
   *  download: number,
   *  request: number
   * }}
   */
  @observable alaCarteCredits = null;

  /**
   * The media stick the user wants to purchase
   *
   * @type {?{
   *  frequency: string,
   *  quantity: number
   * }}
   */
  @observable mediaStickPlan = null;

  /**
   * Chargebee Checkout is Loading
   *
   * @type {boolean}
   */
  @observable isLoadingChargebeeCheckout = false

  /**
   * Promote the plan subscriptions
   *
   * @type {boolean}
   */
  @observable promoteContentPlans = false

  /**
   * Promote the media stick plan
   *
   * @type {boolean}
   */
  @observable promoteMediaStickPlan = false

  /**
   * Chargebee Checkout details
   *
   * @type {{openCheckout: boolean, hostedPage: {}, error: boolean}}
   */
  @observable chargebeeData = {
    openCheckout: false,
    hostedPage: null
  }

  /**
   * The order details billing type selected
   *
   * @type {string}
   */
  @observable billingType = PAYMENT_PLAN_MONTHLY;

  /**
   * Flag for if there is an error while loading chargebee checkout
   *
   * @type {boolean}
   */
  @observable hasChargebeeCheckoutError = false;

  /**
   * Triggered when the component is first mounted to the page.
   */
  @action async componentDidMount() {
    const {apiUserCanUpdatePlanStore, apiUserGetMeStore, apiUserOnboardStore, apiCompanySignGetAllStore} = this.props;

    const {
      planId,
      downloadCreditQty,
      requestCreditQty,
      monthlyMediaStickQty,
      lifetimeMediaStickQty
    } = parseSearchParams(this.props.location.search);

    await apiUserCanUpdatePlanStore.refresh(true);
    await apiUserCanUpdatePlanStore.getPromise();
    const {canUpdatePlan} = apiUserCanUpdatePlanStore.userCanUpdatePlan;

    runInAction('Initialize the section to the first.', () => {
      this.currentSection = 1;
    });

    if (planId && canUpdatePlan) {
      runInAction('Set the starting plan.', () => {
        this.startingPlanId = Number(planId);
        this.promoteMediaStickPlan = true;
      });
    }

    const startingCreditData = {
      download: downloadCreditQty,
      request: requestCreditQty
    };
    if (startingCreditData.download || startingCreditData.request) {
      this.startingAlaCarteCredit = startingCreditData;
      this.alaCarteCredits = startingCreditData;
      this.changeSection(CHECKOUT_SECTION_KEY, true);
    }

    if (monthlyMediaStickQty) {
      this.mediaStickPlan = {
        frequency: PAYMENT_PLAN_MONTHLY,
        quantity: Number(monthlyMediaStickQty)
      };
      this.changeSection(CHECKOUT_SECTION_KEY, true);
    }

    if (lifetimeMediaStickQty) {
      this.mediaStickPlan = {
        frequency: PAYMENT_PLAN_LIFETIME,
        quantity: Number(lifetimeMediaStickQty)
      };
      this.changeSection(CHECKOUT_SECTION_KEY, true);
    }

    apiUserOnboardStore.clearAll();
    apiUserGetMeStore.refresh();
    apiCompanySignGetAllStore.refresh();
  }

  /**
   * Determines update plan message
   *
   * @param {{}} user
   * @returns {string}
   */
  getUpdatePlanAlertMessage = (user) => {
    if (!user || !user.requiresPlanUpdate) {
      return '';
    }

    switch (user.planUpdateReason) {
      case UPDATE_PLAN_REASON_TRIAL:
        return 'Your trial has expired. Please update your plan to continue.';
      case UPDATE_PLAN_REASON_COUPON:
        return 'Your coupon has expired. Please update your plan to continue.';
      case UPDATE_PLAN_REASON_SUBSCRIPTION_DELETED:
        return 'Your subscription has ended. Please update your plan to continue.';
      default:
        return 'Your account requires a plan update. Please choose a plan to continue.';
    }
  }

  /**
   * Changes the currently displayed section
   *
   * @param {string} section
   * @param {boolean} force
   */
  @action changeSection = (section, force) => {
    if (force) {
      this.currentSection = section;
      return;
    }

    if ((section === PLANS_SECTION_KEY)) {
      // reset currentPlan and alaCarteCredit if user backs out of plan page
      this.currentPlan = null;
      this.alaCarteCredits = null;
      this.mediaStickPlan = null;
      this.promoteContentPlans = false;
      this.promoteMediaStickPlan = false;
    }

    if ((section === CHECKOUT_SECTION_KEY) && (!this.currentPlan && !this.alaCarteCredits)) {
      // Don't let the user checkout until they choose a plan or credits.
      return;
    }

    if ((this.currentSection === CHECKOUT_SECTION_KEY && section === ADDONS_SECTION_KEY) && (!this.promoteContentPlans && !this.promoteMediaStickPlan)) {
      // If the user doesn't have any addons available, take them back to the first section.
      this.currentSection = 1;
      return;
    }

    this.currentSection = section;
  };

  /**
   * Change the currently selected plan.
   *
   * @param {{}} plan The plan object from the database.
   */
  @action onPlanSelect = (plan) => {
    const {onboardingStore} = this.props;
    this.currentPlan = plan;
    this.startingPlanId = null;
    this.promoteMediaStickPlan = true;

    onboardingStore.setPlanData(plan);

    this.changeSection(this.currentSection + 1);
  };

  /**
   * Purchase an a la carte credit
   *
   * @param {{
   *  download: number,
  *  request: number
  * }} alaCarteCreditsParam
   */
  @action onPurchaseCredits = (alaCarteCreditsParam) => {
    this.alaCarteCredits = alaCarteCreditsParam;
    this.startingAlaCarteCredit = null;

    this.promoteMediaStickPlan = true;
    this.changeSection(this.currentSection + 1);
  }

  /**
   * Purchase media sticks
   *
   * @param {'monthly' | 'lifetime'} frequency
   * @param {number} quantity
   * @returns {{}}
   */
  @action onPurchaseMediaSticks = (frequency, quantity) => {
    const {apiUserGetMeStore} = this.props;
    const user = apiUserGetMeStore.getFulfilled();
    const companyPlanId = lodash.get(user, 'projectContentCompany.planId');
    this.mediaStickPlan = {
      frequency,
      quantity
    };
    if (companyPlanId) {
      return this.changeSection(CHECKOUT_SECTION_KEY, true);
    }

    if (this.currentSection === PLANS_SECTION_KEY) {
      this.promoteContentPlans = true;
    }

    return this.changeSection(this.currentSection + 1);
  }

  /**
   * On change billing type property
   *
   * @param {string} billingType
   */
  @action onChangeBillingType = (billingType) => {
    this.billingType = billingType;
  }

  /**
   * Control the Chargebee Checkout process
   */
  @action checkoutWithChargebee = async () => {
    const {apiUserOnboardStore} = this.props;

    const data = {
      subscription: null,
      addons: [],
    };

    if (this.currentPlan && this.currentPlan.id) {
      data.subscription = {
        planId: this.currentPlan.id,
        billingType: this.billingType,
      };
    }

    if (this.alaCarteCredits) {
      data.addons = [...data.addons, {
        itemId: ALA_CARTE_CREDITS.download.chargebeeId,
        quantity: this.alaCarteCredits.download
      }, {
        itemId: ALA_CARTE_CREDITS.request.chargebeeId,
        quantity: this.alaCarteCredits.request
      }];
    }

    if (this.mediaStickPlan) {
      data.addons = [...data.addons, {
        itemId: MEDIA_STICK_CREDITS[this.mediaStickPlan.frequency].chargebeeId,
        quantity: this.mediaStickPlan.quantity
      }];
    }

    try {
      runInAction('Set the Chargebee Checkout Loading', () => {
        this.isLoadingChargebeeCheckout = true;
      });

      // reset checkout error
      this.hasChargebeeCheckoutError = false;
      const chargebeeCheckoutResponse = await apiUserOnboardStore.chargebeeCheckout(data);

      runInAction('Set the chargebee hosted page.', () => {
        this.chargebeeData.hostedPage = chargebeeCheckoutResponse;
        this.chargebeeData.openCheckout = true;
      });
    } catch (error) {
      runInAction('Error loading chargebee hosted page.', () => {
        // eslint-disable-next-line no-console
        console.log('Chargebee Error: ', error);
        this.hasChargebeeCheckoutError = true;
      });
    } finally {
      runInAction('Set the Chargebee Checkout Loading', () => {
        this.isLoadingChargebeeCheckout = false;
      });
    }
  }

  @action onSuccessfulChargebeeCheckout = () => {
    const {routerStore, apiUserOnboardStore, onboardingStore, apiUserPollMeStore} = this.props;
    this.chargebeeData.openCheckout = false;

    this.changeSection(1, true);

    // Call the server onboarding.
    apiUserOnboardStore.makeRequest(
      onboardingStore.getDataForServer()
    );
    apiUserPollMeStore.pollThenUpdateMyUser();

    apiUserOnboardStore.getPromise().then(() => {
      routerStore.push(dashboardRoute, {
        replaceIfTo: true,
      });
    });
  }

  /**
   * Calculate additional sign count
   */
  @computed get additionalSignCount() {
    const {apiCompanySignGetAllStore} = this.props;

    const userSigns = apiCompanySignGetAllStore.getFulfilled();
    const signCount = userSigns ? userSigns.length : 0;

    return (signCount > 0) ? (signCount - 1) : 0;
  }

  /**
   * Renders the current section of the onboarding process
   *
   * @returns {{}}
   */
  renderSection() {
    const {currentPlan, chargebeeData, isLoadingChargebeeCheckout} = this;
    const {
      apiUserGetMeStore,
      onboardingStore,
      apiCompanySignGetAllStore,
      apiUserCanUpdatePlanStore
    } = this.props;

    // TODO: maybe need to add something to prevent user from going back when it's saving
    const user = apiUserGetMeStore.getFulfilled();
    onboardingStore.setUserData(user);
    const userSigns = apiCompanySignGetAllStore.getFulfilled();

    const requiresPlanUpdate = user && user.requiresPlanUpdate;
    const updatePlanMessage = this.getUpdatePlanAlertMessage(user);

    // display loading gif until api to determine if user can update plan loads
    if (apiUserCanUpdatePlanStore.state === STATE_PRE || apiUserCanUpdatePlanStore.state === STATE_PENDING) {
      return (
        <LoadingIcon />
      );
    }

    // disable plan buttons if user cannot update plan
    const {canUpdatePlan: userCanUpdatePlan, canUpdateAlaCarte: userCanUpdateAlaCarte, message: cannotUpdatePlanMessage} = apiUserCanUpdatePlanStore.userCanUpdatePlan;

    const submitText = 'Confirm Purchase';
    const submitMethod = this.checkoutWithChargebee;
    const canManageSigns = currentPlan ? currentPlan.canManageSigns : true;

    const contentPlans = (<PricingTabs
      selectPlanId={this.startingPlanId}
      onPlanSelect={this.onPlanSelect}
      canUpdatePlan={userCanUpdatePlan}
      startingCredits={this.startingAlaCarteCredit}
      onPurchaseCredits={this.onPurchaseCredits}
      canUpdateAlaCarte={userCanUpdateAlaCarte}
      user={user}
      tabSelected={this.startingAlaCarteCredit ? 'credits' : 'subscription'}
    />);

    const mediaStickPlans = (<MediaStickPricingTabs
      onPurchase={this.onPurchaseMediaSticks}
      user={user}
    />);

    const SECTION_DICT = {
      [PLANS_SECTION_KEY]: (<div>
        <Link to={dashboardRoute} className="btn btn-default">
          ← Back to Dashboard
        </Link>
        {mediaStickPlans}
        {contentPlans}
      </div>),
      [ADDONS_SECTION_KEY]: (
        <div>
          <h5 className="text-uppercase font-weight-bold">
            OPTIONAL ADD-ONS
          </h5>
          <div className="d-flex justify-content-between mt-3">
            {this.promoteContentPlans && (<div>
              Make your Project Content media player shine with access to our library of over 1000 professionally designed templates.<br />
              Questions? Email our team to schedule a demo: <a href="mailto:info@projectcontent.com">info@projectcontent.com</a>
            </div>)}
            {this.promoteMediaStickPlan && (<div>
              Supercharge your indoor screens with a Project Content media player, turning any indoor screen into a sign.<br />
              Questions? Email our team to schedule a demo: <a href="mailto:info@projectcontent.com">info@projectcontent.com</a>
            </div>)}
            <button
              type="button"
              className="btn btn-primary text-light"
              onClick={() => this.changeSection(this.currentSection + 1, true)}
            >
              Skip →
            </button>
          </div>
          <div className="mt-3">
            {this.promoteMediaStickPlan && mediaStickPlans}
            {this.promoteContentPlans && contentPlans}
          </div>
        </div>
      ),
      [CHECKOUT_SECTION_KEY]: (<div className="row">
        <OnboardUserSigns
          canManageSigns={canManageSigns}
          initializeDefaultSignValues={mediaStickPlans ? 'mediaStickSign' : null}
        />
        {(currentPlan || this.mediaStickPlan || this.alaCarteCredits) && (
          <OrderDetails
            billingType={this.billingType}
            buttonText={submitText}
            isLoading={isLoadingChargebeeCheckout}
            plan={currentPlan}
            onChangeBillingType={this.onChangeBillingType}
            mediaStickPlan={this.mediaStickPlan}
            alaCarteCredits={this.alaCarteCredits}
            onSubmit={submitMethod}
            userSigns={userSigns}
          />
        )}
        <ChargebeeCheckout
          openCheckout={chargebeeData.openCheckout}
          hostedPage={chargebeeData.hostedPage}
          onSuccessfulCheckout={this.onSuccessfulChargebeeCheckout}
        />
      </div>)
    };
    return (
      <div className="container px-0">
        <div className="onboarding-content">
          {!userCanUpdatePlan && (<CannotUpdatePlanAlert message={cannotUpdatePlanMessage} />)}
          {(requiresPlanUpdate && userCanUpdatePlan) && (<UpdatePlanAlert message={updatePlanMessage} />)}

          {SECTION_DICT[this.currentSection]}
        </div>
      </div>
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {currentSection, hasChargebeeCheckoutError} = this;

    return (
      <div id="user-payment-page" className="system-page full-height container-fluid">
        <FlowStepper
          steps={USER_PAYMENT_FLOW_SECTIONS}
          currentStep={currentSection}
          changeStep={this.changeSection}
        />
        <div className="container px-0">
          {(hasChargebeeCheckoutError) && (
            <Alert color="danger" className="signup-alert">
              There was an error loading the checkout page. Please try again. If the problem continues contact us at info@projectcontent.com
            </Alert>
          )}

          {this.renderSection()}
        </div>
      </div>
    );
  }
}

UserPaymentPage.propTypes = {
  location: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
  apiCompanySignGetAllStore: MobxPropTypes.observableObject,
  apiUserCanUpdatePlanStore: MobxPropTypes.observableObject,
  apiUserGetMeStore: MobxPropTypes.observableObject,
  apiUserOnboardStore: MobxPropTypes.observableObject,
  apiUserPollMeStore: MobxPropTypes.observableObject,
  onboardingStore: MobxPropTypes.observableObject,
  routerStore: MobxPropTypes.observableObject,
};

UserPaymentPage.wrappedComponent = {};
UserPaymentPage.wrappedComponent.propTypes = {
  apiCompanySignGetAllStore: MobxPropTypes.observableObject.isRequired,
  apiUserCanUpdatePlanStore: MobxPropTypes.observableObject.isRequired,
  apiUserGetMeStore: MobxPropTypes.observableObject.isRequired,
  apiUserOnboardStore: MobxPropTypes.observableObject.isRequired,
  apiUserPollMeStore: MobxPropTypes.observableObject.isRequired,
  onboardingStore: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(UserPaymentPage)(
  observer(UserPaymentPage)
);
